// Pure date/label helpers shared by the scorecards. No React, no Date-locale
// dependence, so they are safe to unit test with node:test.

const pad2 = (n) => String(n).padStart(2, '0');

/** Returns [y, m, d] numbers from an API date (yyyy-mm-dd prefix), else null. */
function parseYmd(s) {
  if (!s || typeof s !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s.trim());
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  return [y, mo, d];
}

/** "2025-10-27" -> "27/10"; empty string when unparseable. */
export function formatDm(s) {
  const p = parseYmd(s);
  return p ? `${pad2(p[2])}/${pad2(p[1])}` : '';
}

export const RANGE_PLACEHOLDER = '—';

/**
 * "dd/mm – dd/mm"; appends "/yy" to both ends only when the years differ.
 * An incomplete range (either endpoint cleared) yields RANGE_PLACEHOLDER so
 * the date slot is never empty.
 */
export function formatRangeShort(start, end) {
  const a = parseYmd(start);
  const b = parseYmd(end);
  if (!a || !b) return RANGE_PLACEHOLDER;
  const yy = (y) => pad2(y % 100);
  if (a[0] !== b[0]) {
    return `${pad2(a[2])}/${pad2(a[1])}/${yy(a[0])} – ${pad2(b[2])}/${pad2(b[1])}/${yy(b[0])}`;
  }
  return `${pad2(a[2])}/${pad2(a[1])} – ${pad2(b[2])}/${pad2(b[1])}`;
}

/** "dd/mm/yyyy to dd/mm/yyyy" for tooltips. */
export function formatRangeFull(start, end) {
  const a = parseYmd(start);
  const b = parseYmd(end);
  if (!a || !b) return '';
  const f = (p) => `${pad2(p[2])}/${pad2(p[1])}/${p[0]}`;
  return `${f(a)} to ${f(b)}`;
}

/** ISO-8601 week number of a calendar date (UTC arithmetic, no timezone drift). */
function isoWeekOf(y, mo, d) {
  const dt = new Date(Date.UTC(y, mo - 1, d));
  const day = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - day);
  const yearStart = Date.UTC(dt.getUTCFullYear(), 0, 1);
  return Math.ceil(((dt.getTime() - yearStart) / 86400000 + 1) / 7);
}

/**
 * "W44" from "2025-W44", "W44", or a yyyy-mm-dd date. Empty string otherwise.
 */
export function isoWeekLabel(v) {
  if (v == null || typeof v !== 'string') return '';
  const s = v.trim();
  const w = /W(\d{1,2})\b/.exec(s);
  if (w) return `W${pad2(Number(w[1]))}`;
  const p = parseYmd(s);
  return p ? `W${pad2(isoWeekOf(p[0], p[1], p[2]))}` : '';
}

const normKey = (t) => t.replace(/\s+/g, ' ').trim().toLowerCase();

/**
 * Turns caption parts into renderable segments. A part is a string or
 * { text, wrap } where wrap is 'words' (may break between words) or the
 * default 'nowrap' (never broken). Empty / non-text parts are dropped and
 * parts equal to an earlier one (case and whitespace insensitive) are dropped,
 * first wins.
 */
export function captionSegments(parts) {
  const seen = new Set();
  const out = [];
  for (const part of parts || []) {
    const raw = part && typeof part === 'object' ? part.text : part;
    const text =
      typeof raw === 'number' && Number.isFinite(raw) ? String(raw) : typeof raw === 'string' ? raw.trim() : '';
    if (!text) continue;
    const key = normKey(text);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ text, wrap: part && typeof part === 'object' && part.wrap === 'words' ? 'words' : 'nowrap' });
  }
  return out;
}

/** Plain-text form of captionSegments (tooltips, aria): segments joined by " · ". */
export function captionText(parts) {
  return captionSegments(parts).map((s) => s.text).join(' · ');
}

/** Longest text (characters) a caption part keeps unbroken; longer values may wrap at words. */
export const NOWRAP_MAX_CHARS = 28;

/**
 * Caption part for a value: short phrases ('Long Seedless', 'Baltimore,
 * Maryland') stay unbreakable, a longer value may wrap between words. `max`
 * overrides the unbreakable limit for narrow slots (a 28-character nowrap
 * segment cannot fit a half-width tile).
 */
export function captionPart(text, max = NOWRAP_MAX_CHARS) {
  const len = typeof text === 'string' ? text.trim().length : typeof text === 'number' ? String(text).length : 0;
  return { text, wrap: len > max ? 'words' : 'nowrap' };
}

/** Longest unbreakable segment on one end of the range-spread tile (half width). */
export const RANGE_NOWRAP_MAX_CHARS = 18;

/**
 * Meta parts for one end of the range-spread tile: date, the variety of the
 * extreme row (or of the tuple), any other tuple values, then the origin of the
 * extreme last so its long port names wrap last. Exact repeats are dropped by
 * captionSegments.
 */
export function rangeMetaParts(dateText, { origin, variety } = {}, usedTuple) {
  const tuple = usedTuple || {};
  const others = Object.entries(tuple).filter(([k]) => k !== 'variety').map(([, v]) => v);
  return [dateText, variety ?? tuple.variety, ...others, origin].map((t) => captionPart(t, RANGE_NOWRAP_MAX_CHARS));
}
